<template>
    <v-container class="pa-5" fluid>
        <ReportItemsDialog
            :is-open="reportItemsDialog"
            @show-report-items-dialog="showReportItemsDialog"
            @on-close="closeReportItemsDialog"
        />
        <QualitativeNoteDialog
            :is-open="qualitativeNoteDialog"
            :qualitative-note-data="currentQualitativeNote"
            :report-item-plate-data="reportItemPlate"
            :report-item-type-id="reportItemTypeId"
            :report-item-plate-id="reportItemPlateId"
            :qualitative-note-id="qualitativeNoteId"
            @on-close="closeQualitativeNoteDialog"
        />
        <ConfirmDialog
            :data="currentQualitativeNote"
            :is-open="deleteQualitativeNoteDialog"
            :action-loading="qualitativeNoteLoadingOnDelete"
            @on-confirm="deleteQualitativeNote"
            @on-abort="closeDeleteQualitativeNoteDialog"
        >
            <template #title>
                Usuwanie uwagi jakościowej
            </template>
            <template #content>
                Czy na pewno chcesz usunąć uwagę jakościową?
            </template>
            <template #confirmButton>
                Usuń
            </template>
        </ConfirmDialog>

        <TryToCloseStopCardDialog
            v-if="showCloseStopCardDialog"
            :is-open="showCloseStopCardDialog"
            :stop-cards="stopCardsForClosing"
            way-of-closing="UJ"
            @close="showCloseStopCardDialog = false"
        />

        <page-title>Uwagi jakościowe</page-title>

        <v-row align="center">
            <v-col cols="12" sm="4" lg="4">
                <v-btn
                    color="secondary"
                    medium
                    data-sentry-label="Dodaj uwagę jakościową"
                    @click="showReportItemsDialog"
                >
                    <v-icon left>
                        mdi-plus
                    </v-icon>
                    Dodaj uwagę jakościową
                </v-btn>
            </v-col>
            <v-col cols="12" sm="8" lg="8">
                <v-container fluid class="pa-0">
                    <v-row justify="end" dense>
                        <v-col cols="auto">
                            <v-menu
                                v-model="filtersMenu"
                                top
                                left
                                transition="scroll-x-reverse-transition"
                                :close-on-content-click="false"
                            >
                                <template #activator="{on}">
                                    <v-btn
                                        fab
                                        :color="hasFilters ? 'secondary' : 'grey darken-1'"
                                        elevation="1"
                                        small
                                        v-on="on"
                                    >
                                        <v-icon v-if="hasFilters" color="white">
                                            mdi-filter-plus
                                        </v-icon>
                                        <v-icon v-else color="white">
                                            mdi-filter-minus
                                        </v-icon>
                                    </v-btn>
                                </template>
                                <v-card>
                                    <v-container fluid>
                                        <v-row justify="end" dense>
                                            <v-col cols="auto">
                                                <v-btn icon small @click="toggleFiltersMenu()">
                                                    <v-icon color="grey darken-2">
                                                        mdi-close
                                                    </v-icon>
                                                </v-btn>
                                            </v-col>
                                        </v-row>
                                        <v-row>
                                            <v-col cols="12" md="4" lg="3">
                                                <v-autocomplete
                                                    v-model="filters.reportedBy_id"
                                                    :items="reportedByUsers"
                                                    :filter="customUsersFilter"
                                                    :loading="usersSearchReportedByLoading"
                                                    :search-input.sync="usersSearchReportedBy"
                                                    label="Zgłaszający"
                                                    class="v-text-field--placeholder-dark"
                                                    required
                                                    solo
                                                    hide-details
                                                    item-value="id"
                                                    no-data-text="Nie znaleziono"
                                                    clearable
                                                    @keyup.enter="changeFilter"
                                                    @input="changeFilter"
                                                    @click:clear="clearFilter('reportedBy_id')"
                                                >
                                                    <template slot="no-data">
                                                        <v-list-item>
                                                            <v-list-item-title>
                                                                <template v-if="!usersSearchReportedBy">
                                                                    Wpisz treść aby odnaleźć <strong>wprowadzającego</strong>.
                                                                </template>
                                                                <template v-else-if="usersSearchReportedByLoading">
                                                                    Trwa wyszukiwanie...
                                                                </template>
                                                                <template v-else>
                                                                    Nie znaleziono
                                                                </template>
                                                            </v-list-item-title>
                                                        </v-list-item>
                                                    </template>
                                                    <template slot="selection" slot-scope="data">
                                                        <template v-if="data.item.firstName && data.item.lastName">
                                                            {{ data.item.firstName }} {{ data.item.lastName }}
                                                        </template>
                                                        <template v-else>
                                                            {{ data.item.username }}
                                                        </template>
                                                    </template>
                                                    <template slot="item" slot-scope="data">
                                                        <template v-if="data.item.firstName && data.item.lastName">
                                                            {{ data.item.firstName }} {{ data.item.lastName }}
                                                        </template>
                                                        <template v-else>
                                                            {{ data.item.username }}
                                                        </template>
                                                    </template>
                                                </v-autocomplete>
                                            </v-col>
                                            <v-col cols="12" md="4" lg="3">
                                                <v-autocomplete
                                                    v-model="filters.responsiblePerson_id"
                                                    :items="responsibleUsers"
                                                    :filter="customUsersFilter"
                                                    :loading="usersSearchResponsibleLoading"
                                                    :search-input.sync="usersSearchResponsible"
                                                    label="Osoba odpowiedzialna"
                                                    class="v-text-field--placeholder-dark"
                                                    required
                                                    solo
                                                    hide-details
                                                    item-value="id"
                                                    no-data-text="Nie znaleziono"
                                                    clearable
                                                    @keyup.enter="changeFilter"
                                                    @input="changeFilter"
                                                    @click:clear="clearFilter('responsiblePerson_id')"
                                                >
                                                    <template slot="no-data">
                                                        <v-list-item>
                                                            <v-list-item-title>
                                                                <template v-if="!usersSearchReportedBy">
                                                                    Wpisz treść aby odnaleźć <strong>osobę odpowiedzialną</strong>.
                                                                </template>
                                                                <template v-else-if="usersSearchReportedByLoading">
                                                                    Trwa wyszukiwanie...
                                                                </template>
                                                                <template v-else>
                                                                    Nie znaleziono
                                                                </template>
                                                            </v-list-item-title>
                                                        </v-list-item>
                                                    </template>
                                                    <template slot="selection" slot-scope="data">
                                                        <template v-if="data.item.firstName && data.item.lastName">
                                                            {{ data.item.firstName }} {{ data.item.lastName }}
                                                        </template>
                                                        <template v-else>
                                                            {{ data.item.username }}
                                                        </template>
                                                    </template>
                                                    <template slot="item" slot-scope="data">
                                                        <template v-if="data.item.firstName && data.item.lastName">
                                                            {{ data.item.firstName }} {{ data.item.lastName }}
                                                        </template>
                                                        <template v-else>
                                                            {{ data.item.username }}
                                                        </template>
                                                    </template>
                                                </v-autocomplete>
                                            </v-col>
                                            <v-col cols="12" md="4" lg="3">
                                                <v-autocomplete
                                                    v-model="filters.authorizedBy_id"
                                                    :items="authorizedByUsers"
                                                    :filter="customUsersFilter"
                                                    :loading="usersSearchAuthorizedByLoading"
                                                    :search-input.sync="usersSearchAuthorizedBy"
                                                    label="Zatwierdzjący"
                                                    class="v-text-field--placeholder-dark"
                                                    required
                                                    solo
                                                    hide-details
                                                    item-value="id"
                                                    no-data-text="Nie znaleziono"
                                                    clearable
                                                    @keyup.enter="changeFilter"
                                                    @input="changeFilter"
                                                    @click:clear="clearFilter('authorizedBy_id')"
                                                >
                                                    <template slot="no-data">
                                                        <v-list-item>
                                                            <v-list-item-title>
                                                                <template v-if="!usersSearchAuthorizedBy">
                                                                    Wpisz treść aby odnaleźć <strong>zatwierdzającego</strong>.
                                                                </template>
                                                                <template v-else-if="usersSearchAuthorizedByLoading">
                                                                    Trwa wyszukiwanie...
                                                                </template>
                                                                <template v-else>
                                                                    Nie znaleziono
                                                                </template>
                                                            </v-list-item-title>
                                                        </v-list-item>
                                                    </template>
                                                    <template slot="selection" slot-scope="data">
                                                        <template v-if="data.item.firstName && data.item.lastName">
                                                            {{ data.item.firstName }} {{ data.item.lastName }}
                                                        </template>
                                                        <template v-else>
                                                            {{ data.item.username }}
                                                        </template>
                                                    </template>
                                                    <template slot="item" slot-scope="data">
                                                        <template v-if="data.item.firstName && data.item.lastName">
                                                            {{ data.item.firstName }} {{ data.item.lastName }}
                                                        </template>
                                                        <template v-else>
                                                            {{ data.item.username }}
                                                        </template>
                                                    </template>
                                                </v-autocomplete>
                                            </v-col>
                                            <v-col cols="12" md="8" lg="3">
                                                <v-text-field
                                                    v-model="filters.search"
                                                    append-icon="mdi-magnify"
                                                    label="Szukaj..."
                                                    single-line
                                                    clearable
                                                    hide-details
                                                    solo
                                                    @keyup.enter="changeFilter"
                                                    @click:append="changeFilter"
                                                    @click:clear="clearFilter('search')"
                                                />
                                            </v-col>
                                        </v-row>
                                        <v-row>
                                            <v-col cols="12" md="6" lg="3">
                                                <v-select
                                                    v-model="filters.productionProcess_id"
                                                    :items="productionProcesses"
                                                    label="Wybierz proces..."
                                                    item-value="id"
                                                    item-text="name"
                                                    chips
                                                    clearable
                                                    hide-details
                                                    solo
                                                    @change="changeFilter"
                                                />
                                            </v-col>
                                            <v-col cols="12" md="6" lg="3">
                                                <v-select
                                                    v-model="filters.code_id"
                                                    :items="codes"
                                                    label="Wybierz defekt..."
                                                    item-value="id"
                                                    item-text="name"
                                                    clearable
                                                    hide-details
                                                    solo
                                                    @change="changeFilter"
                                                />
                                            </v-col>
                                            <v-col cols="12" md="4" lg="3">
                                                <v-menu
                                                    v-model="menuFrom"
                                                    :close-on-content-click="false"
                                                    :nudge-right="40"
                                                    transition="scale-transition"
                                                    offset-y
                                                    min-width="290px"
                                                >
                                                    <template #activator="{on}">
                                                        <v-text-field
                                                            v-model="filters.createdAt__gte"
                                                            label="Od:"
                                                            prepend-icon="mdi-calendar"
                                                            readonly
                                                            clearable
                                                            solo
                                                            hide-details
                                                            v-on="on"
                                                            @input="changeFilter"
                                                            @click:clear="clearFilter('createdAt__gte')"
                                                        />
                                                    </template>
                                                    <v-date-picker
                                                        v-model="filters.createdAt__gte"
                                                        first-day-of-week="1"
                                                        @change="changeFilter"
                                                        @input="menuFrom = false"
                                                    />
                                                </v-menu>
                                            </v-col>
                                            <v-col cols="12" md="4" lg="3">
                                                <v-menu
                                                    v-model="menuTo"
                                                    :close-on-content-click="false"
                                                    :nudge-right="40"
                                                    transition="scale-transition"
                                                    offset-y
                                                    min-width="290px"
                                                >
                                                    <template #activator="{on}">
                                                        <v-text-field
                                                            v-model="filters.createdAt__lte"
                                                            label="Do:"
                                                            prepend-icon="mdi-calendar"
                                                            readonly
                                                            clearable
                                                            solo
                                                            hide-details
                                                            v-on="on"
                                                            @input="changeFilter"
                                                            @click:clear="clearFilter('createdAt__lte')"
                                                        />
                                                    </template>
                                                    <v-date-picker
                                                        v-model="filters.createdAt__lte"
                                                        first-day-of-week="1"
                                                        @change="changeFilter"
                                                        @input="menuTo = false"
                                                    />
                                                </v-menu>
                                            </v-col>
                                        </v-row>
                                    </v-container>
                                </v-card>
                            </v-menu>
                        </v-col>
                    </v-row>
                </v-container>
            </v-col>
        </v-row>
        <v-row justify="end" no-gutters>
            <v-col cols="auto">
                <v-btn
                    text
                    x-small
                    data-sentry-label="Generuj raport"
                    :disabled="generateReportLoading"
                    @click="generateQualitativeNotesReport"
                >
                    <v-icon
                        v-if="!generateReportLoading"
                        small
                        color="green"
                    >
                        mdi-file-excel
                    </v-icon>
                    <v-progress-circular
                        v-else
                        indeterminate
                        color="primary"
                        class="mr-1"
                        size="12"
                        width="2"
                    />
                    Generuj raport
                </v-btn>
            </v-col>
        </v-row>
        <v-row>
            <v-col cols="12">
                <v-card>
                    <v-data-table
                        :headers="headers"
                        :items="qualitativeNotes"
                        :loading="qualitativeNotesLoading"
                        :footer-props="footerProps"
                        :server-items-length="pagination.itemsLength"
                        :page.sync="pagination.page"
                        :items-per-page.sync="pagination.itemsPerPage"
                        :mobile-breakpoint="0"
                        name="qualitativeNotesData"
                        @pagination="updatePagination"
                    >
                        <template #item.productionProcess="{item}">
                            {{ item.productionProcess.name }}
                        </template>
                        <template #item.code="{item}">
                            {{ item.code.name }}
                        </template>
                        <template #item.reportedBy="{item}">
                            <template v-if="item.reportedBy.firstName && item.reportedBy.lastName">
                                {{ item.reportedBy.firstName }} {{ item.reportedBy.lastName }}
                            </template>
                            <template v-else>
                                {{ item.reportedBy.username }}
                            </template>
                        </template>
                        <template #item.responsiblePeople="{item}">
                            {{ responsiblePeopleList(item.responsiblePeople).at(0) }}
                            <template v-if="item.responsiblePeople.length > 1">
                                <v-tooltip :open-delay="tooltipDelay" top>
                                    <template #activator="{on}">
                                        <v-chip
                                            small
                                            class="ma-1"
                                            color="warning"
                                            v-on="on"
                                        >
                                            +{{ item.responsiblePeople.length - 1 }}
                                        </v-chip>
                                    </template>
                                    <span>{{ responsiblePeopleList(item.responsiblePeople).join(', ') }}</span>
                                </v-tooltip>
                            </template> 
                        </template>
                        <template #item.authorizedBy="{item}">
                            <template v-if="item.authorizedBy.firstName && item.authorizedBy.lastName">
                                {{ item.authorizedBy.firstName }} {{ item.authorizedBy.lastName }}
                            </template>
                            <template v-else>
                                {{ item.authorizedBy.username }}
                            </template>
                        </template>
                        <template #item.createdAt="{item}">
                            {{ formatDate(item.createdAt) }}
                        </template>
                        <template #item.actions="{item}">
                            <div class="d-inline-flex">
                                <v-tooltip :open-delay="tooltipDelay" top>
                                    <template #activator="{on}">
                                        <v-btn
                                            icon
                                            v-on="on"
                                            @click="showQualitativeNoteDialog(item)"
                                        >
                                            <v-icon color="primary">
                                                mdi-ballot
                                            </v-icon>
                                        </v-btn>
                                    </template>
                                    <span>Otwórz</span>
                                </v-tooltip>
                                <v-tooltip :open-delay="tooltipDelay" top>
                                    <template #activator="{on}">
                                        <v-btn
                                            icon
                                            data-sentry-label="Usuń"
                                            v-on="on"
                                            @click="showDeleteQualitativeNoteDialog(item)"
                                        >
                                            <v-icon color="pink">
                                                mdi-delete
                                            </v-icon>
                                        </v-btn>
                                    </template>
                                    <span>Usuń</span>
                                </v-tooltip>
                            </div>
                        </template>
                    </v-data-table>
                </v-card>
            </v-col>
        </v-row>
    </v-container>
</template>

<script>
import PageTitle from '@/components/common/PageTitle'
import ConfirmDialog from '@/components/common/ConfirmDialog'
import ReportItemsDialog from './ReportItemsDialog'
import QualitativeNoteDialog from './QualitativeNoteDialog'
import TryToCloseStopCardDialog from '../Complaints/TryToCloseStopCardDialog'
import filters from '@/mixins/dataTableFilters'
import pagination from '@/mixins/dataTablePagination'
import customFilters from '@/mixins/customFilters'
import dateFormatter from '@/mixins/dateFormatter'
import tooltipDelay from '@/mixins/tooltip'
import { errorHandler, addSentryBreadcrumb } from '@/utils/errorHandler'
import { EventBus } from '@/utils/eventBus'

let searchTimer

export default {
    name: 'QualitativeNotesList',
    components: {
        PageTitle,
        ConfirmDialog,
        ReportItemsDialog,
        QualitativeNoteDialog,
        TryToCloseStopCardDialog,
    },
    mixins: [
        dateFormatter,
        tooltipDelay,
        filters,
        customFilters,
        pagination
    ],
    beforeRouteLeave(to, from, next) {
        if (!this.filtersCleared) {
            this.$store.dispatch('complaints/clearQualitativeNotesFilters')
        }
        this.resetFiltersCleared()
        next()
    },
    data() {
        return {
            filtersMenu: false,
            currentQualitativeNote: {},
            deleteQualitativeNoteDialog: false,
            qualitativeNoteLoadingOnDelete: false,
            qualitativeNoteDialog: false,
            reportItemsDialog: false,
            reportItemPlate: {},
            reportItemTypeId: null,
            reportItemPlateId: null,
            qualitativeNoteId: null,
            menuFrom: false,
            menuTo: false,
            usersSearchReportedBy: null,
            usersSearchReportedByLoading: false,
            usersSearchAuthorizedBy: null,
            usersSearchAuthorizedByLoading: false,
            usersSearchResponsible: null,
            usersSearchResponsibleLoading: false,
            stopCardsForClosing: [],
            showCloseStopCardDialog: false,
            reportedByUsers: [],
            responsibleUsers: [],
            authorizedByUsers: [],
            hasFilters: false,
            generateReportLoading: false,
            headers: [
                {
                    text: 'Numer',
                    value: 'number',
                    sortable: false
                },
                {
                    text: 'Proces',
                    value: 'productionProcess',
                    sortable: false,
                },
                {
                    text: 'Defekt',
                    value: 'code',
                    sortable: false,
                },
                {
                    text: 'Zgłaszający',
                    value: 'reportedBy',
                    sortable: false,
                },
                {
                    text: 'Osoba odpowiedzialna',
                    value: 'responsiblePeople',
                    sortable: false,
                },
                {
                    text: 'Zatwierdzający',
                    value: 'authorizedBy',
                    sortable: false,
                },
                {
                    text: 'Data złożenia',
                    value: 'createdAt',
                    sortable: false,
                },
                {
                    text: 'Akcje',
                    value: 'actions',
                    sortable: false,
                    align: 'center',
                    width: 100
                }
            ],
            user: {},
        }
    },
    computed: {
        userId() {
            return this.$store.state.user.userId
        },
        qualitativeNotes() {
            return this.$store.state.complaints.qualitativeNotes.qualitativeNotes
        },
        qualitativeNotesLoading() {
            return this.$store.state.complaints.qualitativeNotes.qualitativeNotesLoading
        },
        productionProcesses() {
            return this.$store.state.configurations.productProductionProcesses.productionProcesses
        },
        codes() {
            return this.$store.state.complaints.qualitativeNotes.codes
        },
        savedFilters() {
            return this.$store.state.complaints.qualitativeNotes.filters
        },
        filterQuery() {
            const filters = Object.assign({}, this.filters)
            let query = ''

            for (let item in filters) {
                if (filters[item] != null && filters[item] != '') {
                    if (item == 'createdAt__gte') {
                        filters[item] += 'T00:00:00Z'
                    }

                    if (item == 'createdAt__lte') {
                        filters[item] += 'T23:59:59Z'
                    }
                    query = query.concat('&', `${item}=${filters[item]}`)
                }
            }

            return query
        },
    },
    watch: {
        usersSearchReportedBy(value = null) {
            if (value === null) return

            this.usersSearchReportedByLoading = true

            clearTimeout(searchTimer)

            searchTimer = setTimeout(() => {
                if (value) {
                    this.searchUsersReportedBy(value)
                } else {
                    this.usersSearchReportedByLoading = false
                    this.reportedByUsers = []
                }
            }, 500)
        },
        usersSearchResponsible(value = null) {
            if (value === null) return

            this.usersSearchResponsibleLoading = true

            clearTimeout(searchTimer)

            searchTimer = setTimeout(() => {
                if (value) {
                    this.searchUsersResponsible(value)
                } else {
                    this.usersSearchResponsibleLoading = false
                    this.usersSearchResponsible = []
                }
            }, 500)
        },
        usersSearchAuthorizedBy(value = null) {
            if (value === null) return

            this.usersSearchAuthorizedByLoading = true

            clearTimeout(searchTimer)

            searchTimer = setTimeout(() => {
                if (value) {
                    this.searchUsersAuthorizedBy(value)
                } else {
                    this.usersSearchAuthorizedByLoading = false
                    this.authorizedByUsers = []
                }
            }, 500)
        },
    },
    mounted() {
        this.$store.dispatch('toggleMainLoader', true)
        this.$store.dispatch('administration/getUser', this.userId)
            .then(response => {
                this.user = response.data
                if (this.filters.responsiblePerson_id === this.user.id) {
                    this.responsibleUsers.push(this.user)
                    this.checkFilters()
                }
            })
            .finally(() => { this.$store.dispatch('toggleMainLoader', false) })

        this.getQualitativeNotes()

        Promise.all([
            this.$store.dispatch('configurations/getProductionProcesses'),
            this.$store.dispatch('complaints/getCodes')
        ])
            .catch(error => {
                errorHandler(error)
            })
    },
    created() {
        EventBus.$on('closeReportItemsDialog', (clearReportItemPlateOrders) => { this.closeReportItemsDialog(clearReportItemPlateOrders) })
        EventBus.$on('showReportItemsDialog', () => { this.showReportItemsDialog() })
        EventBus.$on('getQualitativeNotes', () => { this.getQualitativeNotes() })
        EventBus.$on('qualitativeNoteCreated', (qualitativeNote) => { this.tryToCloseStopCard(qualitativeNote) })
    },
    destroyed() {
        EventBus.$off()
    },
    methods: {
        toggleFiltersMenu() {
            this.filtersMenu = !this.filtersMenu
        },
        responsiblePeopleList(responsiblePeople) {
            return responsiblePeople?.map(person =>
                person?.user.firstName && person.user.lastName
                    ? `${person.user.firstName} ${person.user.lastName}`
                    : person.user.username
            )
        },
        searchUsersReportedBy(query = '') {
            addSentryBreadcrumb('ui.action', 'Wyszukiwanie użytkownika')

            this.$store.dispatch('administration/findUsers', {
                paginate: '?limit=20',
                query: query
            })
                .then(response => {
                    this.reportedByUsers = response.data.results
                })
                .catch(error => {
                    errorHandler(error)
                })
                .finally(() => {
                    this.usersSearchReportedByLoading = false
                })
        },
        searchUsersResponsible(query = '') {
            addSentryBreadcrumb('ui.action', 'Wyszukiwanie użytkownika')

            this.$store.dispatch('administration/findUsers', {
                paginate: '?limit=20',
                query: query
            })
                .then(response => {
                    this.responsibleUsers = response.data.results
                })
                .catch(error => {
                    errorHandler(error)
                })
                .finally(() => {
                    this.usersSearchResponsibleLoading = false
                })
        },
        searchUsersAuthorizedBy(query = '') {
            addSentryBreadcrumb('ui.action', 'Wyszukiwanie użytkownika')

            this.$store.dispatch('administration/findUsers', {
                paginate: '?limit=20',
                query: query
            })
                .then(response => {
                    this.authorizedByUsers = response.data.results
                })
                .catch(error => {
                    errorHandler(error)
                })
                .finally(() => {
                    this.usersSearchAuthorizedByLoading = false
                })
        },
        getQualitativeNotes() {
            this.$store.dispatch('complaints/getQualitativeNotes', {
                paginate: this.paginate(),
                query: this.filterQuery
            })
                .then(response => {
                    this.handlePaginationResponse(response)
                })
                .catch(error => {
                    errorHandler(error)
                })
        },
        showReportItemsDialog() {
            this.reportItemsDialog = true
        },
        closeReportItemsDialog(clearReportItemPlateOrders) {
            this.reportItemsDialog = false
            if (clearReportItemPlateOrders) this.$store.dispatch('complaints/clearReportItemPlates')
        },
        showDeleteQualitativeNoteDialog(qualitativeNote) {
            this.currentQualitativeNote = qualitativeNote
            this.deleteQualitativeNoteDialog = true
        },
        closeDeleteQualitativeNoteDialog() {
            this.deleteQualitativeNoteDialog = false
            this.currentQualitativeNote = {}
        },
        deleteQualitativeNote() {
            this.qualitativeNoteLoadingOnDelete = true
            this.$store.dispatch('complaints/deleteQualitativeNote', this.currentQualitativeNote.id)
                .then(() => {
                    this.getQualitativeNotes()
                })
                .catch(error => {
                    errorHandler(error)
                })
                .finally(() => {
                    this.closeDeleteQualitativeNoteDialog()
                    this.qualitativeNoteLoadingOnDelete = false
                })
        },
        tryToCloseStopCard(qualitativeNote) {
            if (qualitativeNote.createdStopCards && qualitativeNote.createdStopCards.length > 0) {
                this.stopCardsForClosing = qualitativeNote.createdStopCards
                this.showCloseStopCardDialog = true
            }
        },
        showQualitativeNoteDialog(qualitativeNote) {
            this.currentQualitativeNote = qualitativeNote
            this.reportItemPlateId = qualitativeNote.reportItemPlateId
            this.reportItemTypeId = qualitativeNote.reportItemTypeId
            this.qualitativeNoteId = qualitativeNote.id
            this.qualitativeNoteDialog = true
        },
        closeQualitativeNoteDialog() {
            this.qualitativeNoteDialog = false
            this.currentQualitativeNote = {}
        },
        checkFilters() {
            this.hasFilters = !Object.values(this.filters).every(item => item == null || item.length == 0)
        },
        changeFilter() {
            this.$store.dispatch('complaints/setQualitativeNotesFilters', this.filters)
            this.resetPagination()
            this.getQualitativeNotes()
            this.checkFilters()
        },
        generateQualitativeNotesReport() {
            this.generateReportLoading = true
            this.$store.dispatch('complaints/generateQualitativeNotesReport', {
                query: this.filterQuery,
                userEmail: this.user.email
            })
                .catch(error => {
                    errorHandler(error)
                })
                .finally(() => {
                    this.generateReportLoading = false
                })
        },
        updatePagination(pagination) {
            this.pagination = pagination

            if (pagination.pageCount !== 0 && !this.paginationSettings.paginationPending) {
                this.getQualitativeNotes()
            }
            this.clearPaginationPending()
        }
    }
}
</script>